import { Stack } from '@mui/material';
import { Genres, ProductGroups, Routes } from 'enums';
import FeatureItemAdvanced from 'features/feature-overview/feature-item-advanced';
import ProductGroup from 'features/feature-overview/product-group';
import TextGenerationProjectOverview from 'features/text-generation/text-generation-projects-overview/text-generation-project-overview';
import { useUserData } from 'hooks/use-user-data';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import { isProductHidden } from 'utils/products.utils';

const LeadBooster: React.FC = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { appBarNavigation, setAppBarNavigation } = useAppBarNavigationStore((state) => ({
    appBarNavigation: state.activeTab,
    setAppBarNavigation: state.setActiveTab,
  }));

  const { createdAt, products } = useUserData();

  const leadBoosterGenres = [
    Genres.CONNECTION_REQUEST,
    Genres.SALES_EMAIL,
    Genres.SOCIAL_POST,
    Genres.SOCIAL_AD,
    Genres.INVITATION,
    Genres.MARKETING_NEWSLETTER,
    Genres.PRODUCT_DESCRIPTION,
    Genres.BLOG_POST,
    Genres.LANDING_PAGE,
  ];

  const handleNavigation = (route: Routes, activeTab: NavigationStates = NavigationStates.TOOL) => {
    return () => {
      navigate(route);
      setAppBarNavigation(activeTab);
    };
  };

  return (
    <div className={classes.leadBooster}>
      {appBarNavigation === NavigationStates.TOOL ? (
        <Stack gap={8}>
          <ProductGroup productGroup={ProductGroups.LEAD_BOOSTER} productGroupName={t('leadBoosterSocialSelling')}>
            <FeatureItemAdvanced
              productGroup={ProductGroups.LEAD_BOOSTER}
              featureName={t('leadBoosterAutoCampaignProduct')}
              descriptionPrimary={t('leadBoosterAutoCampaignProductDescriptionMain')}
              descriptionSecondary={t('leadBoosterAutoCampaignProductDescriptionSub')}
              features={[t('fullyAutomated'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
              action={handleNavigation(Routes.LEAD_BOOSTER_CO_PILOT_PROJECTS, NavigationStates.PROJECTS)}
              hot={true}
            />
          </ProductGroup>
          {!isProductHidden(products, [], createdAt) ? (
            <ProductGroup productGroup={ProductGroups.LEAD_BOOSTER} productGroupName={t('leadBoosterMarketingCampaigns')}>
              {!isProductHidden(products, [], createdAt) ? (
                <FeatureItemAdvanced
                  productGroup={ProductGroups.LEAD_BOOSTER}
                  featureName={`${t('marketingCampaignsOptimization')} -\n${t('globalProfiles')}`}
                  descriptionPrimary={t('leadBoosterGlobalProfilesProductDescriptionMain')}
                  descriptionSecondary={t('leadBoosterGlobalProfilesProductDescriptionSub')}
                  features={[t('existingTargetGroups'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
                  action={handleNavigation(Routes.LEAD_BOOSTER_GENERATE_GLOBAL_PROFILE)}
                />
              ) : null}
              {!isProductHidden(products, [], createdAt) ? (
                <FeatureItemAdvanced
                  productGroup={ProductGroups.LEAD_BOOSTER}
                  featureName={`${t('marketingCampaignsOptimization')} -\n${t('tailoredProfiles')}`}
                  descriptionPrimary={t('leadBoosterTailoredProfilesProductDescriptionMain')}
                  descriptionSecondary={t('leadBoosterTailoredProfilesProductDescriptionSub')}
                  features={[t('ownTargetGroups'), t('aiTextGeneration'), t('matchPersonalityProfile')]}
                  linkedinRequired={true}
                  action={handleNavigation(Routes.LEAD_BOOSTER_GENERATE_TAILORED_PROFILE)}
                />
              ) : null}
            </ProductGroup>
          ) : null}
        </Stack>
      ) : appBarNavigation === NavigationStates.PROJECTS ? (
        <Stack gap={{ xs: 2, md: 1 }}>
          {leadBoosterGenres.map((genre) => (
            <TextGenerationProjectOverview key={genre} title={t(genre)} genre={genre} productGroup={ProductGroups.LEAD_BOOSTER} />
          ))}
        </Stack>
      ) : null}
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  leadBooster: {
    padding: '20px 16px 16px 48px',
    [theme.breakpoints.down('lg')]: {
      padding: '20px 8px 8px 8px',
    },
  },
}));

export default LeadBooster;
